import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { X, CheckCircle, ChevronRight } from 'lucide-react';

const TaskAlarmContext = createContext(null);

export const useTaskAlarm = () => {
  const context = useContext(TaskAlarmContext);
  if (!context) {
    throw new Error('useTaskAlarm must be used within TaskAlarmProvider');
  }
  return context;
};

export const TaskAlarmProvider = ({ children }) => {
  const [activeAlarm, setActiveAlarm] = useState(null);
  const [countdown, setCountdown] = useState(10);
  const autoCompleteTimeoutRef = useRef(null);
  const countdownIntervalRef = useRef(null);

  const stopAllTimers = useCallback(() => {
    if (autoCompleteTimeoutRef.current) { clearTimeout(autoCompleteTimeoutRef.current); autoCompleteTimeoutRef.current = null; }
    if (countdownIntervalRef.current) { clearInterval(countdownIntervalRef.current); countdownIntervalRef.current = null; }
    setCountdown(10);
  }, []);

  useEffect(() => { return () => { stopAllTimers(); }; }, [stopAllTimers]);

  const dismissAlarm = useCallback((markComplete = false, alarm = null) => {
    stopAllTimers();
    const currentAlarm = alarm || activeAlarm;
    if (markComplete && currentAlarm?.onComplete) {
      currentAlarm.onComplete(currentAlarm.task.id);
    }
    setActiveAlarm(null);
  }, [activeAlarm, stopAllTimers]);

  const triggerAlarm = useCallback((task, onComplete, nextTask = null) => {
    // Check if notifications are enabled
    const notificationsEnabled = localStorage.getItem('taskNotificationsEnabled');
    if (notificationsEnabled === 'false') {
      // Notifications disabled - just complete without alarm
      if (onComplete) onComplete(task.id);
      return;
    }
    
    stopAllTimers();
    setActiveAlarm({ task, onComplete, nextTask });
    setCountdown(10);

    countdownIntervalRef.current = setInterval(() => {
      setCountdown(prev => Math.max(prev - 1, 0));
    }, 1000);

    autoCompleteTimeoutRef.current = setTimeout(() => {
      if (countdownIntervalRef.current) { clearInterval(countdownIntervalRef.current); countdownIntervalRef.current = null; }
      if (onComplete) onComplete(task.id);
      setActiveAlarm(null);
    }, 10000);
  }, [stopAllTimers]);

  const fmt12 = (time) => {
    if (!time) return '';
    const [h, m] = time.split(':');
    const hour = parseInt(h);
    return `${hour % 12 || 12}:${m} ${hour >= 12 ? 'PM' : 'AM'}`;
  };

  return (
    <TaskAlarmContext.Provider value={{ triggerAlarm, dismissAlarm, activeAlarm }}>
      {children}

      {activeAlarm && (
        <div className="fixed inset-0 z-[999999] pointer-events-none">
          <div className="absolute inset-0 bg-black/50 pointer-events-auto" onClick={() => dismissAlarm(false)} />
          <div className="absolute top-0 left-0 right-0 pointer-events-auto">
            <div className="bg-gradient-to-r from-red-600 via-orange-500 to-red-600 shadow-2xl">
              {/* Main alarm row */}
              <div className="p-4">
                <div className="max-w-4xl mx-auto flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div>
                      <h3 className="text-xl font-bold text-white">Timer Complete!</h3>
                      <p className="text-white/90 text-lg">{activeAlarm.task.title || activeAlarm.task.name}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-center">
                      <div className="text-3xl font-bold text-white">{countdown}</div>
                      <div className="text-xs text-white/70">Auto-complete</div>
                    </div>
                    <button
                      onClick={() => dismissAlarm(true)}
                      className="flex items-center gap-2 px-6 py-3 bg-green-500 hover:bg-green-600 text-white rounded-lg font-bold transition-colors shadow-lg"
                      data-testid="complete-task-alarm-btn"
                    >
                      <CheckCircle size={20} />
                      Complete Now
                    </button>
                    <button
                      onClick={() => dismissAlarm(false)}
                      className="flex items-center gap-2 px-4 py-3 bg-white/20 hover:bg-white/30 text-white rounded-lg font-medium transition-colors"
                      data-testid="dismiss-alarm-btn"
                    >
                      <X size={18} />
                      Dismiss
                    </button>
                  </div>
                </div>
              </div>

              {/* Next task strip */}
              {activeAlarm.nextTask && (
                <div className="bg-black/30 px-4 py-2 border-t border-white/10">
                  <div className="max-w-4xl mx-auto flex items-center gap-2 text-sm">
                    <ChevronRight size={16} className="text-green-300 flex-shrink-0" />
                    <span className="text-white/70">Up next:</span>
                    <span className="font-semibold text-white">
                      {activeAlarm.nextTask.title || activeAlarm.nextTask.name}
                    </span>
                    {activeAlarm.nextTask.due_time && (
                      <span className="text-green-300 font-medium ml-1">
                        at {fmt12(activeAlarm.nextTask.due_time)}
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </TaskAlarmContext.Provider>
  );
};

export default TaskAlarmProvider;
